import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FaceLiveness from './FaceLiveness';
import API_BASE_URL from '../../config';
import './EnrollmentStation.css';

/**
 * EnrollmentStation
 *
 * Flujo completo de registro de rostros por estación:
 *  1. pin        — ingreso del PIN de estación (teclado numérico en pantalla)
 *  2. list       — lista de coordinadores de la estación con estado de rostro
 *  3. scanning   — escaneo facial del coordinador seleccionado
 *  4. success    — confirmación, volver a la lista
 *
 * También permite cambiar el PIN desde la lista.
 *
 * Props:
 *   onClose() — cierra el modal
 */
function EnrollmentStation({ onClose }) {
  const [phase, setPhase]               = useState('pin');   // pin | list | scanning | success | changePin
  const [pinInput, setPinInput]         = useState('');
  const [pinError, setPinError]         = useState(null);
  const [enrollToken, setEnrollToken]   = useState(null);
  const [estacion, setEstacion]         = useState('');
  const [coordinators, setCoordinators] = useState([]);
  const [loadingList, setLoadingList]   = useState(false);
  const [selected, setSelected]         = useState(null);    // coordinador seleccionado
  const [saveError, setSaveError]       = useState(null);
  const [lastSaved, setLastSaved]       = useState(null);    // nombre del último guardado

  // ── Cambio de PIN ────────────────────────────────────────────
  const [cpCurrent, setCpCurrent]   = useState('');
  const [cpNew, setCpNew]           = useState('');
  const [cpNew2, setCpNew2]         = useState('');
  const [cpError, setCpError]       = useState(null);
  const [cpSuccess, setCpSuccess]   = useState(false);
  const [cpLoading, setCpLoading]   = useState(false);

  // ── Teclado numérico ─────────────────────────────────────────
  const pressDigit = (d) => setPinInput((p) => (p.length < 8 ? p + d : p));
  const backspace  = ()  => setPinInput((p) => p.slice(0, -1));
  const clearPin   = ()  => setPinInput('');

  // ── Verificar PIN ────────────────────────────────────────────
  const handleVerifyPin = useCallback(async () => {
    if (pinInput.length < 4) { setPinError('El PIN debe tener al menos 4 dígitos.'); return; }
    setPinError(null);
    try {
      const res = await axios.post(`${API_BASE_URL}api/enrollment/verify-pin`, { pin: pinInput });
      setEnrollToken(res.data.token);
      setEstacion(res.data.estacion);
      setPinInput('');
      setPhase('list');
    } catch (err) {
      setPinError(err.response?.data?.error || 'PIN incorrecto.');
      setPinInput('');
    }
  }, [pinInput]);

  // ── Cargar lista de coordinadores ────────────────────────────
  useEffect(() => {
    if (phase !== 'list' || !enrollToken) return;
    setLoadingList(true);
    axios.get(`${API_BASE_URL}api/enrollment/coordinators`, {
      headers: { Authorization: `Bearer ${enrollToken}` },
    })
      .then((res) => setCoordinators(res.data))
      .catch(() => setCoordinators([]))
      .finally(() => setLoadingList(false));
  }, [phase, enrollToken]);

  // ── Guardar rostro ───────────────────────────────────────────
  const handleLivenessConfirmed = useCallback(async (descriptor) => {
    if (!selected || !enrollToken) return;
    setSaveError(null);
    try {
      await axios.post(
        `${API_BASE_URL}api/enrollment/save-face`,
        { noColaborador: selected.noColaborador, descriptor },
        { headers: { Authorization: `Bearer ${enrollToken}` } }
      );
      setLastSaved(`${selected.nombre} ${selected.apellidos}`);
      setSelected(null);
      setPhase('success');
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Error al guardar. Intenta de nuevo.');
      setPhase('scanning'); // se queda en scanning para reintentar
    }
  }, [selected, enrollToken]);

  // ── Cambiar PIN ──────────────────────────────────────────────
  const handleChangePin = useCallback(async () => {
    if (!cpCurrent || !cpNew || !cpNew2) { setCpError('Completa todos los campos.'); return; }
    if (cpNew !== cpNew2) { setCpError('Los PINs nuevos no coinciden.'); return; }
    if (cpNew.length < 4) { setCpError('El nuevo PIN debe tener al menos 4 dígitos.'); return; }
    setCpError(null);
    setCpLoading(true);
    try {
      await axios.post(`${API_BASE_URL}api/enrollment/change-pin`, {
        estacion,
        currentPin: cpCurrent,
        newPin: cpNew,
      });
      setCpSuccess(true);
      setCpCurrent(''); setCpNew(''); setCpNew2('');
    } catch (err) {
      setCpError(err.response?.data?.error || 'Error al cambiar PIN.');
    } finally {
      setCpLoading(false);
    }
  }, [cpCurrent, cpNew, cpNew2, estacion]);

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="enroll-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="enroll-modal">

        {/* Header compartido */}
        <div className="enroll-header">
          <div className="enroll-header-left">
            {(phase === 'list' || phase === 'changePin') && (
              <button className="enroll-back" onClick={() => {
                if (phase === 'changePin') { setCpError(null); setCpSuccess(false); setPhase('list'); }
                else { setEnrollToken(null); setEstacion(''); setPhase('pin'); }
              }}>
                ←
              </button>
            )}
            {phase === 'scanning' && (
              <button className="enroll-back" onClick={() => { setSelected(null); setSaveError(null); setPhase('list'); }}>←</button>
            )}
            {phase === 'success' && (
              <button className="enroll-back" onClick={() => setPhase('list')}>←</button>
            )}
          </div>
          <div className="enroll-header-center">
            <span className="enroll-title">
              {phase === 'pin'       && 'Registro de coordinadores'}
              {phase === 'list'      && `Estación ${estacion}`}
              {phase === 'scanning'  && `Escaneando: ${selected?.nombre}`}
              {phase === 'success'   && '¡Registrado!'}
              {phase === 'changePin' && 'Cambiar PIN'}
            </span>
          </div>
          <button className="enroll-close" onClick={onClose}>✕</button>
        </div>

        {/* ── FASE: PIN ──────────────────────────────────────── */}
        {phase === 'pin' && (
          <div className="enroll-body">
            <p className="enroll-hint">Ingresa el PIN de tu estación para continuar</p>

            {/* Display del PIN */}
            <div className="enroll-pin-display">
              {Array.from({ length: Math.max(pinInput.length, 4) }).map((_, i) => (
                <div key={i} className={`enroll-pin-dot ${i < pinInput.length ? 'filled' : ''}`} />
              ))}
            </div>

            {pinError && <div className="enroll-error">{pinError}</div>}

            {/* Teclado */}
            <div className="enroll-numpad">
              {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((key, i) => (
                <button
                  key={i}
                  className={`enroll-key ${key === '' ? 'enroll-key-empty' : ''}`}
                  disabled={key === ''}
                  onClick={() => {
                    if (key === '⌫') backspace();
                    else pressDigit(String(key));
                  }}
                >
                  {key}
                </button>
              ))}
            </div>

            <button
              className="enroll-btn-primary"
              onClick={handleVerifyPin}
              disabled={pinInput.length < 4}
            >
              Ingresar
            </button>
          </div>
        )}

        {/* ── FASE: LISTA ────────────────────────────────────── */}
        {phase === 'list' && (
          <div className="enroll-body">
            {loadingList ? (
              <div className="enroll-center"><div className="enroll-spinner" /></div>
            ) : (
              <>
                <div className="enroll-list-header">
                  <span className="enroll-list-count">{coordinators.length} coordinadores</span>
                  <button className="enroll-change-pin-btn" onClick={() => setPhase('changePin')}>
                    🔑 Cambiar PIN
                  </button>
                </div>

                <div className="enroll-list">
                  {coordinators.map((c) => (
                    <button
                      key={c.noColaborador}
                      className="enroll-coord-row"
                      onClick={() => { setSelected(c); setSaveError(null); setPhase('scanning'); }}
                    >
                      <div className="enroll-coord-info">
                        <span className="enroll-coord-name">{c.nombre} {c.apellidos}</span>
                        <span className="enroll-coord-sub">#{c.noColaborador} · {c.puesto || 'Coordinador'}</span>
                      </div>
                      <div className={`enroll-face-badge ${c.hasFace ? 'has-face' : 'no-face'}`}>
                        {c.hasFace ? '✓ Rostro' : 'Sin rostro'}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── FASE: SCANNING ─────────────────────────────────── */}
        {phase === 'scanning' && (
          <div className="enroll-body">
            {saveError && <div className="enroll-error" style={{ marginBottom: 12 }}>{saveError}</div>}
            <FaceLiveness
              title=""
              onConfirmed={handleLivenessConfirmed}
              onError={(e) => setSaveError(e)}
            />
            <p className="enroll-scan-hint">
              Pide a <strong>{selected?.nombre}</strong> que mire la cámara y gire la cabeza cuando aparezca la flecha
            </p>
          </div>
        )}

        {/* ── FASE: SUCCESS ───────────────────────────────────── */}
        {phase === 'success' && (
          <div className="enroll-body enroll-center">
            <div className="enroll-success-icon">✅</div>
            <h3 className="enroll-success-title">Rostro registrado</h3>
            <p className="enroll-success-sub">
              <strong>{lastSaved}</strong> ya puede iniciar sesión con reconocimiento facial.
            </p>
            <button className="enroll-btn-primary" onClick={() => setPhase('list')}>
              Registrar otro coordinador
            </button>
          </div>
        )}

        {/* ── FASE: CAMBIAR PIN ──────────────────────────────── */}
        {phase === 'changePin' && (
          <div className="enroll-body">
            {cpSuccess ? (
              <div className="enroll-center">
                <div className="enroll-success-icon">✅</div>
                <h3 className="enroll-success-title">PIN actualizado</h3>
                <p className="enroll-success-sub">El nuevo PIN está activo para la estación {estacion}.</p>
                <button className="enroll-btn-primary" onClick={() => { setCpSuccess(false); setPhase('list'); }}>
                  Volver
                </button>
              </div>
            ) : (
              <>
                <p className="enroll-hint">Estación: <strong>{estacion}</strong></p>
                {cpError && <div className="enroll-error">{cpError}</div>}

                <div className="enroll-field">
                  <label>PIN actual</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    placeholder="••••"
                    value={cpCurrent}
                    onChange={(e) => setCpCurrent(e.target.value)}
                  />
                </div>
                <div className="enroll-field">
                  <label>Nuevo PIN</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    placeholder="Mínimo 4 dígitos"
                    value={cpNew}
                    onChange={(e) => setCpNew(e.target.value)}
                  />
                </div>
                <div className="enroll-field">
                  <label>Confirmar nuevo PIN</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    placeholder="Repite el nuevo PIN"
                    value={cpNew2}
                    onChange={(e) => setCpNew2(e.target.value)}
                  />
                </div>

                <button
                  className="enroll-btn-primary"
                  onClick={handleChangePin}
                  disabled={cpLoading}
                >
                  {cpLoading ? 'Guardando...' : 'Cambiar PIN'}
                </button>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default EnrollmentStation;
